import type { GeckoCase } from 'types'

import {
  createEmptyMdCaseFeedback as createEmptyMdCaseFeedbackSchema,
  createEmptyMdCaseFeedbackSection as createEmptyMdCaseFeedbackSectionSchema,
  isMdCaseRecord,
  MD_CASE_PROMOTION_STATES as MD_CASE_PROMOTION_STATES_SCHEMA,
  MD_CASE_SEVERITIES as MD_CASE_SEVERITIES_SCHEMA,
  MD_CASE_SOURCE_TYPES as MD_CASE_SOURCE_TYPES_SCHEMA,
  MD_CASE_STATUSES as MD_CASE_STATUSES_SCHEMA,
  MD_CASE_WORKFLOW_DOMAIN as MD_CASE_WORKFLOW_DOMAIN_SCHEMA,
  MD_CASE_WORKFLOW_MODES as MD_CASE_WORKFLOW_MODES_SCHEMA,
  normalizeMdCaseRecord,
} from './mdCaseSchema'

export type MdCase = import('./mdCaseSchema').MdCase
export type MdCaseAgent = import('./mdCaseSchema').MdCaseAgent
export type MdCaseAlertItem = import('./mdCaseSchema').MdCaseAlertItem
export type MdCaseAlerts = import('./mdCaseSchema').MdCaseAlerts
export type MdCaseAnnotations = import('./mdCaseSchema').MdCaseAnnotations
export type MdCaseFeedback = import('./mdCaseSchema').MdCaseFeedback
export type MdCaseFeedbackSection =
  import('./mdCaseSchema').MdCaseFeedbackSection
export type MdCaseFeedbackSections =
  import('./mdCaseSchema').MdCaseFeedbackSections
export type MdCaseMeta = import('./mdCaseSchema').MdCaseMeta
export type MdCaseNormalizationError =
  import('./mdCaseSchema').MdCaseNormalizationError
export type MdCaseNormalizationResult =
  import('./mdCaseSchema').MdCaseNormalizationResult
export type MdCaseNotes = import('./mdCaseSchema').MdCaseNotes
export type MdCasePiTagReference = import('./mdCaseSchema').MdCasePiTagReference
export type MdCasePromotionState = import('./mdCaseSchema').MdCasePromotionState
export type MdCaseReferences = import('./mdCaseSchema').MdCaseReferences
export type MdCaseSeverity = import('./mdCaseSchema').MdCaseSeverity
export type MdCaseSourceType = import('./mdCaseSchema').MdCaseSourceType
export type MdCaseStatus = import('./mdCaseSchema').MdCaseStatus
export type MdCaseTransitionValidationInput =
  import('./mdCaseSchema').MdCaseTransitionValidationInput
export type MdCaseTransitionValidationResult =
  import('./mdCaseSchema').MdCaseTransitionValidationResult
export type MdCaseWorkflow = import('./mdCaseSchema').MdCaseWorkflow
export type MdCaseWorkflowMode = import('./mdCaseSchema').MdCaseWorkflowMode

export const MD_CASE_WORKFLOW_DOMAIN = MD_CASE_WORKFLOW_DOMAIN_SCHEMA
export const MD_CASE_WORKFLOW_MODES = MD_CASE_WORKFLOW_MODES_SCHEMA
export const MD_CASE_SOURCE_TYPES = MD_CASE_SOURCE_TYPES_SCHEMA
export const MD_CASE_SEVERITIES = MD_CASE_SEVERITIES_SCHEMA
export const MD_CASE_PROMOTION_STATES = MD_CASE_PROMOTION_STATES_SCHEMA
export const MD_CASE_STATUSES = MD_CASE_STATUSES_SCHEMA
export const createEmptyMdCaseFeedback = createEmptyMdCaseFeedbackSchema
export const createEmptyMdCaseFeedbackSection =
  createEmptyMdCaseFeedbackSectionSchema
export const isMdCase = isMdCaseRecord
export const normalizeMdCase = normalizeMdCaseRecord

const MD_CASE_ALLOWED_TRANSITIONS: Readonly<
  Record<MdCaseStatus, MdCaseStatus[]>
> = {
  'New Alert': ['Open', 'Cleared', 'Shelved'],
  Open: ['Resolved', 'Cleared', 'Shelved'],
  Resolved: ['Closed', 'Shelved'],
  Closed: ['Open'],
  Cleared: ['Open'],
  Shelved: ['Open'],
}

const parseNullableString = (value: unknown): string | null => {
  if (typeof value !== 'string') return null
  const trimmedValue = value.trim()
  return trimmedValue.length > 0 ? trimmedValue : null
}

export const isMdCaseStatus = (value: unknown): value is MdCaseStatus =>
  typeof value === 'string' && MD_CASE_STATUSES.includes(value as MdCaseStatus)

export const toMdCase = (caseEntry: GeckoCase | null | undefined) => {
  const result = normalizeMdCaseRecord(caseEntry)
  return result.isMdCase && result.hasCoreFields ? result.value : null
}

export const canTransitionMdCaseStatus = (
  from: MdCaseStatus,
  to: MdCaseStatus
): boolean => MD_CASE_ALLOWED_TRANSITIONS[from].includes(to)

export const validateMdCaseTransition = ({
  from,
  to,
  reopenReason,
}: MdCaseTransitionValidationInput): MdCaseTransitionValidationResult => {
  const normalizedReopenReason = parseNullableString(reopenReason)
  const requiresReopenReason =
    to === 'Open' && (from === 'Closed' || from === 'Cleared')

  if (!canTransitionMdCaseStatus(from, to)) {
    return {
      isValid: false,
      requiresReopenReason,
      normalizedReopenReason,
      error: `Invalid M&D case transition: ${from} -> ${to}`,
    }
  }

  if (requiresReopenReason && normalizedReopenReason === null) {
    return {
      isValid: false,
      requiresReopenReason: true,
      normalizedReopenReason: null,
      error: 'Reopening an M&D case requires a reason',
    }
  }

  return {
    isValid: true,
    requiresReopenReason,
    normalizedReopenReason: requiresReopenReason
      ? normalizedReopenReason
      : null,
    error: null,
  }
}
